import { Link } from 'react-router-dom';
import type { ContentItem } from '@/types/content';

interface ContentCardProps {
  item: ContentItem;
  showType?: boolean;
}

const typeLabel: Record<string, string> = {
  movie: '电影',
  tvshow: '剧集',
  news: '资讯',
};

export default function ContentCard({ item, showType = true }: ContentCardProps) {
  const meta =
    item.type === 'news'
      ? `${item.date} · ${item.articleCategory}`
      : `${item.year} · ${item.genre.slice(0, 2).join(' / ')}`;

  return (
    <Link
      to={`/detail/${item.id}`}
      className="group block cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
      data-product-shop
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-background-200">
        <img
          src={item.cover}
          alt={`${item.title} 海报`}
          title={`${item.title} 影视作品封面`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-foreground-950/90 via-foreground-950/25 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <p className="line-clamp-3 text-xs leading-relaxed text-background-50/95">{item.summary}</p>
          <span className="mt-2 flex items-center gap-1 font-label text-[11px] font-semibold text-accent-300">
            查看详情
            <i className="ri-arrow-right-line text-[12px]"></i>
          </span>
        </div>

        <div className="absolute left-2 top-2 flex items-center gap-1">
          {showType && (
            <span className="rounded-sm bg-background-50/92 px-1.5 py-0.5 font-label text-[10px] font-semibold tracking-wide text-foreground-800">
              {typeLabel[item.type]}
            </span>
          )}
          {typeof item.rating === 'number' && item.rating > 0 && (
            <span className="flex items-center gap-0.5 rounded-sm bg-accent-500 px-1.5 py-0.5 font-label text-[10px] font-bold text-foreground-950">
              <i className="ri-star-fill text-[9px]"></i>
              {item.rating.toFixed(1)}
            </span>
          )}
        </div>

        {item.status && (
          <span
            className={`absolute right-2 top-2 rounded-sm px-1.5 py-0.5 font-label text-[10px] font-semibold ${
              item.status === '连载'
                ? 'bg-primary-500 text-background-50'
                : 'bg-secondary-500 text-background-50'
            }`}
          >
            {item.status}
          </span>
        )}
      </div>

      <div className="px-3 pb-3.5 pt-3">
        <h3 className="truncate font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
          {item.title}
        </h3>
        <p className="mt-1.5 truncate font-label text-[11px] text-foreground-500">{meta}</p>
        {item.type !== 'news' && item.seasons ? (
          <p className="mt-1 text-[11px] text-foreground-500">全 {item.seasons} 季 · {item.episodes} 集</p>
        ) : null}
      </div>
    </Link>
  );
}